/**
 * Content for the public landing page at `/`.
 *
 * Plain data, so the copy can be edited without touching the markup. Every
 * claim here must match what the product actually does — the module grid is
 * derived from the shared module catalogue (SRS 5.5) rather than typed twice,
 * and the rest follows the README's "What is built" table. Never over-promise.
 */
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  Ambulance,
  Baby,
  BarChart3,
  BedDouble,
  Bell,
  Building2,
  ClipboardList,
  CreditCard,
  Droplet,
  FileSignature,
  FileText,
  FlaskConical,
  FolderArchive,
  HeartPulse,
  Landmark,
  Layers,
  ListTodo,
  Lock,
  MessageSquare,
  Package,
  Pill,
  Receipt,
  ScanLine,
  ScrollText,
  Scissors,
  ShieldCheck,
  Smartphone,
  Smile,
  Sparkles,
  Stethoscope,
  ToggleLeft,
  UserPlus,
  Users,
  UtensilsCrossed,
  Waves,
} from 'lucide-react';
import { MODULE_CATALOGUE, type ModuleCode, type ModuleDescriptor } from '@his/shared';
import type { PublicHomeStats } from '@his/shared';

export type ModuleGroup = ModuleDescriptor['group'];

/* ---------------------------------- hero ---------------------------------- */

export type HeroStatKey = Extract<
  keyof PublicHomeStats,
  'hospitals' | 'patients' | 'encounters' | 'admissions'
>;

export const HERO_STATS: readonly { key: HeroStatKey; label: string }[] = [
  { key: 'hospitals', label: 'Hospitals live' },
  { key: 'patients', label: 'Patients registered' },
  { key: 'encounters', label: 'OPD consultations' },
  { key: 'admissions', label: 'Admissions' },
];

/** The "today" strip in the console preview card. */
export const TODAY_META: readonly { key: keyof PublicHomeStats; label: string }[] = [
  { key: 'patients', label: 'Patients registered' },
  { key: 'encounters', label: 'OPD consultations' },
  { key: 'admissions', label: 'Admissions' },
  { key: 'labOrders', label: 'Lab orders' },
];

/* --------------------------------- modules -------------------------------- */

const INTEGRATION_MODULES: readonly ModuleCode[] = ['nphies', 'wasfaty'];

export const MODULE_ICONS: Record<ModuleCode, LucideIcon> = {
  core: Layers,
  opd: Stethoscope,
  ipd: BedDouble,
  er: HeartPulse,
  ot: Scissors,
  blood_bank: Droplet,
  pharmacy: Pill,
  inventory: Package,
  lab: FlaskConical,
  radiology: ScanLine,
  billing: Receipt,
  insurance: CreditCard,
  accounting: Landmark,
  hr: Users,
  mrd: FolderArchive,
  reports: BarChart3,
  notifications: Bell,
  ambulance: Ambulance,
  dietary: UtensilsCrossed,
  housekeeping: Sparkles,
  feedback: MessageSquare,
  tasks: ListTodo,
  maternity: Baby,
  dialysis: Waves,
  daycare: Activity,
  dental: Smile,
  portal: Smartphone,
  nphies: ShieldCheck,
  wasfaty: FileSignature,
};

export const MODULE_GROUP_META: Record<ModuleGroup, { title: string; blurb: string }> = {
  core: { title: 'Core platform', blurb: 'Always on — identity, tenancy, RBAC, audit and the patient master index.' },
  clinical: { title: 'Clinical', blurb: 'The wards, the theatre and the specialties.' },
  diagnostics: { title: 'Diagnostics', blurb: 'Laboratory and radiology, with analyser and modality interfacing.' },
  commerce: { title: 'Commerce', blurb: 'Money in, stock out, and the ledger that reconciles both.' },
  admin: { title: 'Administration', blurb: 'The back office and the support services around the wards.' },
  patient: { title: 'Patient-facing', blurb: 'What the patient sees on their own phone.' },
};

/** Display order for the grid; the catalogue itself is unordered by group. */
export const MODULE_GROUP_ORDER: readonly ModuleGroup[] = [
  'clinical',
  'diagnostics',
  'commerce',
  'admin',
  'patient',
  'core',
];

export interface ModuleGroupSection {
  group: ModuleGroup;
  title: string;
  blurb: string;
  modules: ModuleDescriptor[];
}

/** The module catalogue, grouped for display — one source of truth, no retyping. */
export const MODULE_GROUPS: readonly ModuleGroupSection[] = MODULE_GROUP_ORDER.map((group) => ({
  group,
  ...MODULE_GROUP_META[group],
  modules: MODULE_CATALOGUE.filter((m) => m.group === group && !INTEGRATION_MODULES.includes(m.code)),
}));

export const MODULE_COUNT = MODULE_CATALOGUE.filter((m) => !INTEGRATION_MODULES.includes(m.code)).length;

/* --------------------------------- workflow ------------------------------- */

export interface WorkflowStep {
  index: string;
  title: string;
  description: string;
  icon: LucideIcon;
}

/** The patient journey, in the order the wards see it. */
export const WORKFLOW_STEPS: readonly WorkflowStep[] = [
  { index: '01', title: 'Registration', description: 'One MRN per patient, found again by name, phone or CNIC — never registered twice.', icon: UserPlus },
  { index: '02', title: 'OPD or Emergency', description: 'Token queue and consultation, or rapid registration and triage in ER.', icon: Stethoscope },
  { index: '03', title: 'Diagnostics', description: 'Lab and radiology orders flow to the worklists; results release after verification.', icon: FlaskConical },
  { index: '04', title: 'IPD or Theatre', description: 'Bed board, CPOE and eMAR on the ward; OT scheduling and operative records.', icon: BedDouble },
  { index: '05', title: 'Pharmacy', description: 'E-prescriptions dispense from batch-tracked stock — retail counter or ward indent.', icon: Pill },
  { index: '06', title: 'Billing & discharge', description: 'One invoice across every department, panel co-pay split, discharge summary.', icon: Receipt },
];

/* --------------------------------- platform ------------------------------- */

export interface PlatformPoint {
  icon: LucideIcon;
  title: string;
  description: string;
}

/** Each of these maps to a row in the README's "How the invariants are enforced" table. */
export const PLATFORM_POINTS: readonly PlatformPoint[] = [
  {
    icon: Building2,
    title: 'Multi-tenant, isolated twice',
    description:
      'Every query is rewritten for the tenant, and Postgres row-level security enforces it again underneath — the app role owns nothing.',
  },
  {
    icon: Lock,
    title: 'Default-deny RBAC',
    description:
      'No endpoint ships without a permission. Roles are cloned from templates, so an upgrade never widens a customised role.',
  },
  {
    icon: ScrollText,
    title: 'Hash-chained audit trail',
    description:
      'Database triggers record every clinical and financial change; the log rejects UPDATE and DELETE outright.',
  },
  {
    icon: ToggleLeft,
    title: 'Per-hospital module licensing',
    description:
      'Modules are switched on per tenant with a dependency engine. What is not licensed is hidden, refused by the API, and degraded gracefully.',
  },
  {
    icon: ShieldCheck,
    title: 'Two-factor sign-in',
    description:
      'TOTP is mandated per role; the owner console has no password at all — the authenticator alone is the key.',
  },
  {
    icon: ClipboardList,
    title: 'Money that adds up',
    description:
      'Integer minor units end to end, idempotent payments, gapless numbering, and a general ledger the invoices post into.',
  },
];

/* --------------------------------- sign-in -------------------------------- */

export interface SignInSurface {
  label: string;
  to: string;
  description: string;
}

/** Every public door into the product, for the footer. The owner console has none — `/owner` is reached by typing it, never by a link. */
export const SIGN_IN_SURFACES: readonly SignInSurface[] = [
  { label: 'Staff sign-in', to: '/login', description: 'Hospital staff — your hospital, email and password.' },
  { label: 'Account managers', to: '/login?mode=client', description: 'Contact people — tickets and renewals.' },
  { label: 'Patient portal', to: '/portal', description: 'Patients — sign in with a phone OTP.' },
  { label: 'Find care', to: '/find', description: 'The public directory of hospitals, labs and pharmacies.' },
];

export const FOOTER_ICON = FileText;

/* ------------------------- offline distribution -------------------------- */

/** Hero buttons (plan §3.2): sign in, download the installer, check a local server's version. */
export const HERO_ACTIONS = {
  signIn: 'Sign in',
  enterConsole: 'Enter console',
  download: 'Download offline installer',
  checkVersion: 'Check my version',
} as const;

/** Deep link into the local server's Settings → About tab. */
export const ABOUT_SETTINGS_ROUTE = '/admin/settings?tab=about';

export const DOWNLOAD_COPY = {
  eyebrow: 'Offline installer',
  title: 'Run HIS on your own server',
  lead:
    'Download the signed release package, activate it once online, and run the whole hospital on your own Windows server and LAN — the cloud keeps a read-only mirror.',
  latestLabel: 'Latest stable release',
  betaLabel: 'Beta channel',
  betaHint: 'A newer beta is available — for hospitals that agreed to test early builds.',
  noRelease: 'No release has been published yet. Check back soon.',
  authTitle: 'Download the package',
  authHint:
    'The download needs your hospital code and the download PIN. Your hospital administrator finds the PIN under Settings → About & updates; your provider can also issue it from the Owner console.',
  tenantSlugLabel: 'Hospital code',
  pinLabel: 'Download PIN',
  downloadButton: 'Download',
  verifying: 'Checking your PIN…',
  rateLimited: 'Too many wrong PINs from this network — wait 15 minutes and try again.',
  invalid: 'The hospital code or download PIN is not right.',
  offlineNotEnabled: 'Offline mode is not enabled for this hospital — ask the owner to allow it.',
  agentsTitle: 'Helper tools',
  agentsLead: 'Workstation helpers that ride inside every package and can also be downloaded on their own.',
  agentsEmpty: 'No helper downloads are published on this server.',
  manualTitle: 'Installing or updating by hand',
  manualSteps: [
    'Download the .lpk above on any PC and copy it to the server.',
    'New server: unzip the package and run install.ps1 as Administrator with the activation token your provider issued.',
    'Existing server: open PowerShell as Administrator and run C:\\HIS\\app\\deploy\\local-install\\update.ps1 -Package <path to the .lpk>.',
    'The updater verifies the signature, backs up the database, installs, migrates and restarts — and rolls back on its own if anything fails.',
  ],
} as const;

export const CHECK_VERSION_COPY = {
  eyebrow: 'Check my version',
  title: 'Is your local server up to date?',
  lead:
    'Enter the address of your HIS server on the hospital network. This page asks it which version it runs and compares that with the latest published release — nothing is installed from here.',
  addressLabel: 'Local server address',
  addressPlaceholder: 'http://his.local:3001',
  button: 'Check now',
  checking: 'Asking the server…',
  upToDate: 'Up to date',
  runningLabel: 'Running',
  latestLabel: 'Latest',
  openAbout: 'Open the update screen on the server',
  openAboutHint: 'Sign in there as an administrator and press Update now — the server downloads the package, backs up, installs and restarts itself.',
  unreachableTitle: 'Could not reach that server from this browser',
  unreachableHint:
    'Check the address, make sure this PC is on the hospital network and that the server allows this page to query it. You can still update by hand:',
  lockedLabel: 'Read-only',
} as const;

export const LOCAL_SERVER_DEFAULT_ADDRESS = 'http://his.local:3001';
export const LOCAL_SERVER_ADDRESS_STORAGE_KEY = 'his.landing.localServerAddress';
